/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 *
 * Quick Access tiles -- phone-style. GhAppTile is one service (rounded icon
 * + label + a small live-status dot). GhFolderTile is a group, drawn as an
 * iOS-like folder: a rounded tray holding a mini-grid of the real service
 * icons it contains.
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Service, checkServiceStatus, getServiceUptime } from "@/api/services";
import { SERVICE_ICONS } from "@/constants/serviceIcons";
import { SERVICES_REFRESH_INTERVALS } from "@/constants/servicesRefreshIntervals";
import { ServiceIcon } from "../ServiceIcon";

function hasRealIcon(s: Service): boolean {
  if (s.iconType === "preset") return Boolean(s.iconValue && SERVICE_ICONS[s.iconValue]);
  if (s.iconType === "url") return Boolean(s.iconValue);
  return false;
}

function monogram(name: string): string {
  const parts = name.replace(/[^\p{L}\p{N} ]/gu, "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.trim().slice(0, 2).toUpperCase() || "?";
}

/** The glyph on a full-size app icon: brand icon / favicon when configured,
 *  otherwise a two-letter name monogram (nicer than a wall of globes). */
const AppGlyph: React.FC<{ service: Service }> = ({ service }) => {
  if (hasRealIcon(service)) {
    return <ServiceIcon service={service} className="h-6 w-6" />;
  }
  return <span className="gh-qa-mono">{monogram(service.name)}</span>;
};

/** A single cell in a folder's mini preview -- deliberately tiny: brand
 *  icon, or just the first letter for icon-less services. */
const MiniGlyph: React.FC<{ service: Service }> = ({ service }) => {
  if (hasRealIcon(service)) {
    return <ServiceIcon service={service} className="h-full w-full" />;
  }
  return <span className="gh-qa-mini-mono">{service.name.trim().charAt(0).toUpperCase() || "?"}</span>;
};

interface GhAppTileProps {
  service: Service;
  editing: boolean;
  dragHandle?: Record<string, unknown>;
  onEdit: (s: Service) => void;
  onDelete: (s: Service) => void;
}

export const GhAppTile: React.FC<GhAppTileProps> = ({
  service,
  editing,
  dragHandle,
  onEdit,
  onDelete,
}) => {
  const { data: status, isFetching } = useQuery({
    queryKey: ["service-status", service.id],
    queryFn: () => checkServiceStatus(service.id),
    refetchInterval: SERVICES_REFRESH_INTERVALS.STATUS,
    retry: false,
  });
  const { data: uptimeSummaries } = useQuery({
    queryKey: ["service-uptime"],
    queryFn: getServiceUptime,
    refetchInterval: SERVICES_REFRESH_INTERVALS.UPTIME,
    retry: false,
  });
  const uptime = uptimeSummaries?.[service.id];
  const state: "up" | "down" | "idle" =
    !status && isFetching ? "idle" : status?.online ? "up" : "down";
  const title =
    state === "idle"
      ? service.name
      : `${service.name} — ${
          state === "up" ? (typeof uptime === "number" ? `${uptime.toFixed(1)}% uptime` : "up") : "down"
        }`;

  const icon = (
    <span className="gh-qa-ico">
      <AppGlyph service={service} />
    </span>
  );

  if (editing) {
    return (
      <div className="gh-qa-tile gh-qa-tile--edit">
        <span className="gh-qa-hit" {...(dragHandle ?? {})}>
          {icon}
        </span>
        <span className="gh-qa-name">{service.name}</span>
        <span className="gh-qa-tools">
          <button type="button" className="gh-qa-tool" onClick={() => onEdit(service)} aria-label="Edit service">
            <PencilIcon className="h-3 w-3" />
          </button>
          <button type="button" className="gh-qa-tool" onClick={() => onDelete(service)} aria-label="Delete service">
            <TrashIcon className="h-3 w-3" />
          </button>
        </span>
      </div>
    );
  }

  return (
    <a
      className="gh-qa-tile"
      href={service.url}
      target="_blank"
      rel="noopener noreferrer"
      title={title}
    >
      {icon}
      <span className="gh-qa-name">{service.name}</span>
    </a>
  );
};

interface GhFolderTileProps {
  name: string;
  services: Service[];
  editing: boolean;
  dragHandle?: Record<string, unknown>;
  onOpen: () => void;
}

export const GhFolderTile: React.FC<GhFolderTileProps> = ({
  name,
  services,
  editing,
  dragHandle,
  onOpen,
}) => {
  const preview = services.slice(0, 4);
  const tray = (
    <span className="gh-qa-folder">
      <span className="gh-qa-folder-mini">
        {preview.map((s) => (
          <span key={s.id} className="gh-qa-folder-cell">
            <MiniGlyph service={s} />
          </span>
        ))}
      </span>
    </span>
  );

  if (editing) {
    return (
      <div className="gh-qa-tile gh-qa-tile--edit">
        <span className="gh-qa-hit" {...(dragHandle ?? {})}>
          {tray}
        </span>
        <span className="gh-qa-name">{name}</span>
        <span className="gh-qa-folder-n">{services.length}</span>
      </div>
    );
  }

  return (
    <button type="button" className="gh-qa-tile" onClick={onOpen} title={`${name} · ${services.length}`}>
      {tray}
      <span className="gh-qa-name">{name}</span>
    </button>
  );
};
